import { FC, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { Class } from '../../models/Class';
import { getClassCardData } from '../../businessLogicService/getClassCardData';
import ClassesRosterCard from './ClassesRosterCard';
import ClassesRosterCleverInfo from './ClassesRosterCleverInfo';
import { ClassMenuAction } from './ClassesRosterCardMenu';

interface Props {
  classes: Class[];
  isAdmin?: boolean;
  onCreateClass: () => void;
  onMenuAction?: (action: ClassMenuAction, classId: string) => void;
  /** Highlights the very first card in the grid to nudge the teacher to start there. */
  highlightFirstClass?: boolean;
}

const PAGE_SIZE = 9;

type SortOption = 'name-asc' | 'name-desc' | 'grade-asc' | 'grade-desc';

const SORT_OPTIONS: { label: string; value: SortOption }[] = [
  { label: 'Name: A → Z', value: 'name-asc' },
  { label: 'Name: Z → A', value: 'name-desc' },
  { label: 'Grade: Low → High', value: 'grade-asc' },
  { label: 'Grade: High → Low', value: 'grade-desc' },
];

const ClassesRosterGrid: FC<Props> = ({
  classes,
  isAdmin = false,
  onCreateClass,
  onMenuAction,
  highlightFirstClass = false,
}) => {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [first, setFirst] = useState(0);

  const siteOptions = useMemo(() => {
    const byId = new Map<string, string>();
    classes.forEach(cls => cls.site?.id && byId.set(cls.site.id, cls.site.name ?? ''));
    return Array.from(byId, ([value, label]) => ({ value, label }));
  }, [classes]);

  const cards = useMemo(() => {
    const term = search.trim().toLowerCase();
    let data = classes.map(getClassCardData);

    if (isAdmin && selectedSites.length) {
      data = data.filter(card => selectedSites.includes(card.siteId));
    }

    if (term) {
      data = data.filter(card => {
        const fields = [card.name, card.section, card.gradeLabel, ...(isAdmin ? [card.ownerName] : [])];
        return fields.some(field => field.toLowerCase().includes(term));
      });
    }

    return [...data].sort((a, b) => {
      switch (sortBy) {
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'grade-asc':
          return a.gradeSortValue - b.gradeSortValue;
        case 'grade-desc':
          return b.gradeSortValue - a.gradeSortValue;
        case 'name-asc':
        default:
          return a.name.localeCompare(b.name);
      }
    });
  }, [classes, search, sortBy, selectedSites, isAdmin]);

  useEffect(() => setFirst(0), [search, sortBy, selectedSites]);

  const pageCards = cards.slice(first, first + PAGE_SIZE);
  const hasCleverClass = useMemo(() => classes.some(cls => Boolean(cls.externalSync?.source)), [classes]);
  // No visible classes but the grid still rendered → the user's classes are all hidden.
  const allHidden = classes.length === 0;

  const searchPlaceholder = isAdmin
    ? "Search classes by name, section, grade or teacher's name"
    : 'Search classes by name, section or grade';

  return (
    <div className="flex flex-column w-full gap-md" style={{ marginBottom: '2.5rem' }}>
      <div className="nc_classes-header flex flex-column md:flex-row md:align-items-center md:justify-content-between">
        <div className="flex flex-column md:flex-row md:align-items-center flex-grow-1">
          {isAdmin && (
            <MultiSelect
              value={selectedSites}
              options={siteOptions}
              onChange={event => setSelectedSites(event.value)}
              placeholder="All sites"
              selectedItemsLabel="{0} sites"
              maxSelectedLabels={1}
              className="nc_classes-site-filter"
              aria-label="Filter by site"
            />
          )}
          <IconField iconPosition="left" className="nc_classes-search flex-grow-1">
            <InputIcon className="nc_icon-nc_search icon-sm"> </InputIcon>
            <InputText
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              className="w-full"
              aria-label={searchPlaceholder}
            />
          </IconField>
        </div>

        <div className="flex flex-column md:flex-row md:align-items-center gap-lg">
          {hasCleverClass && <ClassesRosterCleverInfo />}
          <Button
            onClick={onCreateClass}
            label="Create new class"
            icon="nc_icon-nc_add icon-sm"
            rounded
            aria-label="Create new class"
            className="tw-w-full md:tw-w-auto"
          />
        </div>
      </div>

      {!allHidden && (
        <>
          {isAdmin ? (
            <div className="flex align-items-center justify-content-between gap-md">
              <div className="flex align-items-center gap-sm">
                <span className="font-semibold text-sm">Sort by</span>
                <Dropdown value={sortBy} options={SORT_OPTIONS} onChange={event => setSortBy(event.value)} />
              </div>
              <span className="font-semibold">
                {cards.length} {cards.length === 1 ? 'class' : 'classes'}
              </span>
            </div>
          ) : (
            <span className="font-semibold">
              {cards.length} {cards.length === 1 ? 'class' : 'classes'}
            </span>
          )}
          <hr className="nc_class-card__divider w-full m-0" />
        </>
      )}

      {allHidden ? (
        <div className="flex flex-column align-items-center justify-content-center text-center gap-sm py-8">
          <h3 className="m-0">Nothing here yet</h3>
          <p className="m-0 tw-text-neutral-700">
            Your classes are all hidden right now.
            <br />
            Unhide one from below, or create a new class.
          </p>
        </div>
      ) : cards.length ? (
        <div className="grid">
          {pageCards.map((card, index) => (
            <div key={card.id} className="col-12 lg:col-6 xl:col-4">
              <ClassesRosterCard
                data={card}
                isAdmin={isAdmin}
                onMenuAction={onMenuAction}
                highlighted={highlightFirstClass && first === 0 && index === 0}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center py-6 tw-text-neutral-700">No classes match your search.</p>
      )}

      {cards.length > PAGE_SIZE && (
        <Paginator
          first={first}
          rows={PAGE_SIZE}
          totalRecords={cards.length}
          onPageChange={(event: PaginatorPageChangeEvent) => setFirst(event.first)}
          template="PrevPageLink PageLinks NextPageLink"
          className="nc_classes-paginator justify-content-end"
        />
      )}
    </div>
  );
};

export default ClassesRosterGrid;
